import i18next from 'i18next';
import { OneIdEnvironment } from '../../hooks/useOneIdEnvironment';
import {
  NavigationData,
  AppsV1NavigationSuccessResponse,
  AppsV1IdentityResponse,
  GenesisUserInfoResponse,
  TenantInfo,
  NavigationItem,
  AppSection,
  AppsV1NavigationMenuItem,
} from '../../types';
import { getProfileConfigs } from '../utils/profile-configs';
import {
  RevenueNavigationResponse,
  ZuoraRevenueStorageKeys,
} from '../../hooks/useRevenueNavigation';
import { RevenueNavigationTransformer } from '../transformers/revenue-transformer';
import { BillingNavigationTransformer } from '../transformers/billing-transformer';
import { AppSectionFactory } from '../factories/app-factory';
import { UserDataExtractor } from '../utils/user-data-extractor';
import { NavigationInsertionItem } from '../../hooks/useBillingNavigation';
import {
  EnvironmentTier,
  getEnvironmentTier,
} from '../../utils/getEnvironmentTier';
import { mapIdToIcon } from '../utils/mappers';
import { Variant } from '../hooks/useVariant';
import { OrgHierarchyResponse } from '../../hooks/useMultiOrgHierarchy';
import { convertToBillingUrl } from '../../utils/getBillingBaseUrl';
import {
  PAYMENTS_INSIGHTS_PATH,
  PAYMENTS_OVERVIEW_MENU_ID,
  rewritePaymentsOverviewMenuUrl,
} from '../../utils/paymentsOverviewRoute';

export class NavigationAdapter {
  // private static readonly HIDDEN_MENU_SECTIONS = [
  //   'Zuora/AdvancedPayments', // Legacy payments section, replaced by Zuora/Payments
  // ]; // keeping it for future reference.

  private environmentPrefix: OneIdEnvironment['revenuePrefix'];
  private isRevenueApp: boolean;
  private variant: Variant;
  constructor(
    environmentPrefix: OneIdEnvironment['revenuePrefix'] = '/revenue',
    isRevenueApp = false,
    variant: Variant,
  ) {
    this.environmentPrefix = environmentPrefix;
    this.isRevenueApp = isRevenueApp;
    this.variant = variant;
  }

  async transform(
    billingData?: AppsV1NavigationSuccessResponse | null | undefined,
    identityData?: AppsV1IdentityResponse | null,
    permissions?: GenesisUserInfoResponse | null,
    billingEntities?: TenantInfo[],
    revenueEntities?: TenantInfo[],
    allEntities?: TenantInfo[],
    revenueData?: RevenueNavigationResponse,
    multiOrgHierarchy?: OrgHierarchyResponse,
    customObjectNavItems?: AppsV1NavigationMenuItem[],
    addSolutionMenuItems?: (
      menus: AppsV1NavigationMenuItem[],
    ) => AppsV1NavigationMenuItem[],
    reinvented?: boolean,
    isPaymentLinkEnabled = false,
  ): Promise<NavigationData> {
    try {
      // Check what data we actually have
      const hasRevenueData = revenueData && (revenueEntities ?? []).length > 0;
      const hasBillingData = billingData && (billingEntities ?? []).length > 0;

      console.log('has billing data', hasBillingData);

      console.log('🔧 NavigationAdapter.transform called with:', {
        billingData,
        hasBillingData,
        hasRevenueData,
        hasIdentityData: !!identityData,
        hasPermissionsData: !!permissions,
        billingEntitiesCount: billingEntities?.length || 0,
        revenueEntitiesCount: revenueEntities?.length || 0,
      });

      // Try the full transformation first (your existing logic)
      return await this.buildFullNavigation(
        billingData ?? null,
        identityData ?? null,
        permissions ?? null,
        billingEntities ?? [],
        revenueEntities ?? [],
        allEntities ?? [],
        revenueData,
        multiOrgHierarchy,
        customObjectNavItems,
        addSolutionMenuItems,
        reinvented,
        isPaymentLinkEnabled,
      );
    } catch (error) {
      console.error(
        '❌ Full navigation build failed, using safe fallback:',
        error,
      );
      return this.getFallbackData();
      // Use safe navigation builders instead of getFallbackData()
      // return this.buildNavigation(
      //   billingEntities ?? [],
      //   revenueEntities ?? [],
      //   identityData ?? null,
      //   revenueData,
      // );
    }
  }

  private async buildFullNavigation(
    billingData: AppsV1NavigationSuccessResponse | null,
    identityData: AppsV1IdentityResponse | null,
    permissions: GenesisUserInfoResponse | null,
    billingEntities: TenantInfo[],
    revenueEntities: TenantInfo[],
    allEntities: TenantInfo[],
    revenueData?: RevenueNavigationResponse,
    multiOrgHierarchy?: OrgHierarchyResponse,
    customObjectNavItems?: AppsV1NavigationMenuItem[],
    addSolutionMenuItems?: (
      menus: AppsV1NavigationMenuItem[],
    ) => AppsV1NavigationMenuItem[],
    reinvented?: boolean,
    isPaymentLinkEnabled = false,
  ): Promise<NavigationData> {
    let billingResult: {
      secondaryItems: NavigationItem[];
      bottomItems: NavigationItem[];
    } = { secondaryItems: [], bottomItems: [] };
    let revenueResult: {
      secondaryItems: NavigationItem[];
      bottomItems: NavigationItem[];
    } = { secondaryItems: [], bottomItems: [] };

    if (billingData) {
      // Dynamically build navigationInsertionItems based on permissions
      const ALLOWED_ENVIRONMENTS = [
        EnvironmentTier.STG,
        EnvironmentTier.DEV,
        EnvironmentTier.SBX,
        EnvironmentTier.PREPROD,
        EnvironmentTier.PROD,
      ];

      const hasCatalogUIPermission = permissions?.permissions?.includes?.(
        'permission.CatalogUI',
      );

      const hasPaymentOrchestrationPermission =
        !!permissions?.permissions?.includes?.(
          'permission.paymentOrchestration',
        );
      // Transaction only available in the reinvented condition - refer ZPY-458 figma
      const isTransactionsAvailable: boolean = reinvented || false;

      const hasFeaturesEntitlementsV2Permission =
        !!permissions?.permissions?.includes?.(
          'permission.FeaturesEntitlementsV2',
        );
      const hasArbiterAuthorPermission = !!permissions?.permissions?.includes?.(
        'permission.ArbiterAuthor',
      );
      const hasArbiterReviewerPermission =
        !!permissions?.permissions?.includes?.('permission.ArbiterReviewer');
      // Either ArbiterAuthor (read+write+delete) or ArbiterReviewer (read-only) can access rules
      const hasArbiterReadPermission =
        hasArbiterAuthorPermission || hasArbiterReviewerPermission;
      const hasConfigurableBundlePermission =
        !!permissions?.permissions?.includes?.('permission.ConfigurableBundle');
      const hasBundlingPermission = !!permissions?.permissions?.includes?.(
        'permission.Bundling',
      );
      const hasFeaturesEntitlementsPermission =
        !!permissions?.permissions?.includes?.(
          'permission.FeaturesEntitlements',
        );

      const isCatalogProductsMenuEnabled =
        !!hasCatalogUIPermission &&
        ALLOWED_ENVIRONMENTS.includes(getEnvironmentTier());

      const navigationInsertionItems: NavigationInsertionItem[] = [];
      if (isCatalogProductsMenuEnabled) {
        console.debug(
          'NavigationAdapter: CatalogUI permission and environment allowed — using Products catalog menu',
        );
      }

      const isInsightsEnabled = false; // when this is enabled, this will replave Overview. Below paymentLinksTargetId logic also needs correction during that change.

      const paymentLinksTargetId = hasPaymentOrchestrationPermission
        ? isInsightsEnabled
          ? 'Zuora/Payments/Insights'
          : 'Zuora/Payments/Checkout'
        : PAYMENTS_OVERVIEW_MENU_ID;

      const transactionsTargetId =
        hasPaymentOrchestrationPermission && isPaymentLinkEnabled
          ? 'Zuora/Payments/PaymentLinks'
          : paymentLinksTargetId;

      if (hasPaymentOrchestrationPermission) {
        navigationInsertionItems.push(
          {
            targetId: PAYMENTS_OVERVIEW_MENU_ID,
            menuItem: {
              id: 'Zuora/Payments/Connections',
              label: i18next.t('Payment.Connections.TabLabel'),
              url: '/platform/apps/payments/connections',
              parentId: 'Zuora/Payments',
              parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
            },
            enabled: true,
          },
          {
            targetId: 'Zuora/Payments/Connections',
            menuItem: {
              id: 'Zuora/Payments/Routing',
              label: i18next.t('Payment.Routing.TabLabel'),
              url: '/platform/apps/payments/routing',
              parentId: 'Zuora/Payments',
              parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
            },
            enabled: true,
          },
          {
            targetId: 'Zuora/Payments/Routing',
            menuItem: {
              id: 'Zuora/Payments/Checkout',
              label: i18next.t('Payment.Checkout.TabLabel'),
              url: '/platform/apps/payments/checkout',
              parentId: 'Zuora/Payments',
              parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
            },
            enabled: true,
          },
          {
            // This will replace the PaymentsOverview once integrated. Changes to be made to replace oerview menu with this menu
            // ZPY-248: Insights in progress — links to Checkout until InsightsPage is ready
            targetId: 'Zuora/Payments/Checkout',
            menuItem: {
              id: 'Zuora/Payments/Insights',
              label: i18next.t('Payment.Insights.TabLabel'),
              url: '/platform/apps/payments/checkout',
              parentId: 'Zuora/Payments',
              parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
            },
            enabled: isInsightsEnabled,
          },
        );
      }

      navigationInsertionItems.push(
        {
          targetId: paymentLinksTargetId,
          menuItem: {
            id: 'Zuora/Payments/PaymentLinks',
            label: i18next.t('Payment.PaymentLinks.TabLabel'),
            url: '/platform/apps/dynamic-hpf/paymentLinks',
            parentId: 'Zuora/Payments',
            parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
          },
          enabled: hasPaymentOrchestrationPermission && isPaymentLinkEnabled,
        },
        {
          targetId: transactionsTargetId,
          menuItem: {
            id: 'Zuora/Payments/Transactions',
            label: i18next.t('Payment.Transactions.TabLabel'),
            url: '/platform/apps/com_zuora/transactions',
            parentId: 'Zuora/Payments',
            parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
          },
          enabled: isTransactionsAvailable,
        },
        {
          // ZPY-248: Retry on-click behavior TBD — placeholder route until confirmed
          targetId: 'Zuora/Payments/PaymentSchedules',
          menuItem: {
            id: 'Zuora/Payments/Retry',
            label: i18next.t('Payment.Retry.TabLabel'),
            url: '/platform/apps/payments/retry',
            parentId: 'Zuora/Payments',
            parentLabel: i18next.t('GlobalNav.Insertion.Payments'),
          },
          enabled: false,
        },
      );

      if (hasFeaturesEntitlementsV2Permission) {
        navigationInsertionItems.push({
          targetId: 'Zuora/Customers/CustomerAccounts',
          menuItem: {
            id: 'Zuora/Customers/EntitlementDashboard',
            label: 'Entitlements',
            url: '/platform/apps/entitlements-dashboard',
            parentId: 'Zuora/Customers',
            parentLabel: 'Customers',
          },
          enabled: true,
        });
      }

      const isDataMigrationEnabled =
        getEnvironmentTier() === EnvironmentTier.STG;
      const dataMigrationMenuItem: AppsV1NavigationMenuItem = {
        id: 'Zuora/CustomerAdmin/DataMigration',
        label: i18next.t('GlobalNav.Insertion.DataMigration'),
        url: '/services/data-migration/',
        parentId: 'Zuora/CustomerAdmin',
        parentLabel: i18next.t('GlobalNav.Insertion.Administration'),
      };

      // doing minimal changes to revert the visibility of advancedPayments for now.
      const menusWithoutAdvancedPayments = billingData.menus;

      // ZPY-248: Remove legacy Payments and Refunds items — both are replaced by Transactions (ZPY-458).
      // This is only applicable to the new UI. if userpreference.reinvented is true, below should be done. Else transactions should be hidden.
      const menusWithoutLegacyPayments = isTransactionsAvailable
        ? menusWithoutAdvancedPayments.filter(
            (m) =>
              m.id !== 'Zuora/Payments/PaymentsPayments' &&
              m.id !== 'Zuora/Payments/Refunds',
          )
        : menusWithoutAdvancedPayments;

      const menusWithDataMigration =
        !isDataMigrationEnabled ||
        menusWithoutLegacyPayments.some(
          (m) => m.id === dataMigrationMenuItem.id,
        )
          ? menusWithoutLegacyPayments
          : [...menusWithoutLegacyPayments, dataMigrationMenuItem];

      const menusWithPaymentsOverviewRoute = rewritePaymentsOverviewMenuUrl(
        menusWithDataMigration,
        hasPaymentOrchestrationPermission,
        undefined,
        i18next.t('Payment.Insights.TabLabel'),
      );

      const billingTransformer = new BillingNavigationTransformer();
      billingResult = await billingTransformer.transform(
        {
          ...billingData,
          menus: menusWithPaymentsOverviewRoute,
        },
        customObjectNavItems,
        addSolutionMenuItems,
        navigationInsertionItems, // Pass the dynamically built array
        isCatalogProductsMenuEnabled
          ? {
              includeCatalog: true,
              includeEntitlements: hasFeaturesEntitlementsPermission,
              includeArbiterGraphLinks: hasArbiterReadPermission,
              includeConfigurableBundles: hasConfigurableBundlePermission,
              includeFixedBundles: hasBundlingPermission,
              includeFeatures: hasFeaturesEntitlementsPermission,
            }
          : null,
      );

      billingResult = {
        ...billingResult,
        secondaryItems: hasPaymentOrchestrationPermission
          ? billingResult.secondaryItems.map((item) => {
              if (item.id !== 'Zuora/Payments' || !item.popover?.items) {
                return item;
              }

              return {
                ...item,
                popover: {
                  ...item.popover,
                  items: item.popover.items.map((popoverItem) =>
                    popoverItem.id === PAYMENTS_OVERVIEW_MENU_ID
                      ? {
                          ...popoverItem,
                          menuPath: [
                            PAYMENTS_INSIGHTS_PATH,
                            ...(popoverItem.menuPath ?? []),
                          ],
                        }
                      : popoverItem,
                  ),
                },
              };
            })
          : billingResult.secondaryItems,
      };
    }

    if (revenueData) {
      const revenueTransformer = new RevenueNavigationTransformer(
        this.environmentPrefix,
      );
      revenueResult = revenueTransformer.transform(revenueData);
    }

    const paymentsItems = this.extractPaymentsItems(
      billingResult.secondaryItems,
    );
    const showPaymentsApp =
      paymentsItems.length > 0 && this.variant === Variant.BILLING;

    const apps = this.createAppSections(
      billingEntities,
      revenueEntities,
      allEntities,
      billingResult.secondaryItems,
      revenueResult.secondaryItems,
      permissions,
      billingData,
      multiOrgHierarchy,
      paymentsItems,
      showPaymentsApp,
    );

    const revenueFirstName = window.localStorage.getItem('ZRevenue_FirstName');
    const revenueLastName = window.localStorage.getItem('ZRevenue_LastName');
    const revenueEmail = window.localStorage.getItem('ZRevenue_Email');
    // Written by Revenue host after multi-role selection — may arrive after
    // the first nav transform (see useNavigationData authUserRole hash).
    const revenueRole = window.localStorage.getItem(
      ZuoraRevenueStorageKeys.AuthUserRole,
    );
    const revenueUserName = window.localStorage.getItem('ZRevenue_UserName');

    // Billing + OTR use identity; OneID Revenue uses Revenue localStorage.
    const userData: any =
      identityData && (!this.isRevenueApp || this.variant === Variant.OTR)
        ? UserDataExtractor.extract(identityData, !this.isRevenueApp)
        : {
            firstName: revenueFirstName,
            lastName: revenueLastName,
            userName: revenueUserName,
            email: revenueEmail,
            role: revenueRole,
            profileConfig: getProfileConfigs()['revenue'],
          };

    return {
      version: '1.0.0',
      primary: {
        top: this.createPrimaryTopItems(
          billingEntities,
          revenueEntities,
          allEntities,
          revenueData,
          showPaymentsApp,
        ),
        bottom: this.isRevenueApp
          ? revenueResult.bottomItems
          : billingResult.bottomItems,
        user: userData || {
          firstName: userData?.firstName || '',
          lastName: userData?.lastName || '',
          userName: userData?.userName || '',
          email: userData?.email || '',
          role: userData?.role || '',
          profile: userData?.profile || '',
          logout: userData?.logout || '',
        },
      },
      secondary: {
        apps,
      },
    };
  }

  private createPrimaryTopItems(
    billingEntities: TenantInfo[],
    revenueEntities: TenantInfo[],
    allEntities: TenantInfo[],
    revenueData?: RevenueNavigationResponse,
    showPaymentsApp = false,
  ): NavigationItem[] {
    const billingTenantIds =
      billingEntities.length > 0
        ? new Set(billingEntities.map((entity) => entity.tenantId))
        : undefined;
    const revenueTenantIds =
      revenueEntities.length > 0
        ? new Set(revenueEntities.map((entity) => entity.tenantId))
        : undefined;
    const currentEntityPath = this.findCurrentEntityPath(allEntities);
    const currentEntity =
      currentEntityPath && currentEntityPath.length
        ? currentEntityPath[currentEntityPath.length - 1]
        : undefined;

    const fallbackBillingEntity =
      this.isRevenueApp && billingEntities.length > 0
        ? this.findNearestAncestorMatching(currentEntityPath, (entity) =>
            billingTenantIds ? billingTenantIds.has(entity.tenantId) : true,
          )
        : undefined;
    const billingEntityForRevenueApp = fallbackBillingEntity ?? null;

    const fallbackRevenueEntity =
      !this.isRevenueApp && revenueEntities.length > 0 && currentEntity
        ? this.findFirstDescendantMatching(currentEntity, (entity) =>
            revenueTenantIds ? revenueTenantIds.has(entity.tenantId) : true,
          )
        : undefined;
    const revenueEntityForBillingApp = fallbackRevenueEntity ?? null;

    const items: NavigationItem[] = [
      {
        label: i18next.t('GlobalNav.Apps.ZuoraHome'),
        icon: 'home',
        href: this.isRevenueApp
          ? `${this.environmentPrefix}/#/dashboard/initialDashboard`
          : convertToBillingUrl('/platform/webapp'),
        originalHref: this.isRevenueApp
          ? `${this.environmentPrefix}/#/dashboard/initialDashboard`
          : convertToBillingUrl('/platform/apps/lighthouse-homepage'),
        divider: true,
        id: 'zuora-home',
      },
    ];
    // Only add apps if entities exist
    if (billingEntities.length > 0) {
      console.log('✅ Adding Billing app to safe primary navigation');

      if (this.isRevenueApp) {
        // In revenue app: billing should have href to navigate away
        const billingEntity = billingEntityForRevenueApp;
        if (billingEntity) {
          items.push({
            label: i18next.t('GlobalNav.Apps.ZuoraBilling'),
            icon: 'billing',
            href: billingEntity.href,
            id: 'zuora-billing',
          });
        }
      } else {
        // In billing app: billing should have appName (current app)
        items.push({
          label: i18next.t('GlobalNav.Apps.ZuoraBilling'),
          icon: 'billing',
          appName: 'billing',
          id: 'zuora-billing',
        });
      }

      // ZPY-248: Payments primary rail entry mirrors the Payments app section visibility.
      if (showPaymentsApp) {
        items.push({
          label: i18next.t('GlobalNav.Apps.ZuoraPayments'),
          icon: 'payments',
          appName: 'payments',
          id: 'zuora-payments',
        });
      }
    }

    if (revenueEntities.length > 0) {
      console.log('✅ Adding Revenue app to safe primary navigation');

      if (this.isRevenueApp) {
        // In revenue app: revenue should have appName (current app)
        items.push({
          label: i18next.t('GlobalNav.Apps.ZuoraRevenue'),
          icon: 'revenue',
          appName: 'revenue',
          id: 'zuora-revenue',
          lock: true,
        });
      } else {
        // In billing app: revenue should have href to navigate away
        const revenueEntity = revenueEntityForBillingApp; // Prefer linked tenant when available
        if (revenueEntity) {
          items.push({
            label: i18next.t('GlobalNav.Apps.ZuoraRevenue'),
            icon: 'revenue',
            id: 'zuora-revenue',
            lock: true,
            href:
              revenueEntity.href ||
              `${this.environmentPrefix}/#/dashboard/closeprocess`,
          });
        }
      }
    } else if (revenueData && this.isRevenueApp) {
      items.push({
        label: i18next.t('GlobalNav.Apps.ZuoraRevenue'),
        icon: 'revenue',
        appName: 'revenue',
        id: 'zuora-revenue',
        lock: true,
      });
    }

    // Removing for now
    // items.push(this.createMoreMenu());

    return items;
  }

  private createMoreMenu(): NavigationItem {
    return {
      label: i18next.t('GlobalNav.Apps.MoreZuoraApps'),
      icon: 'more',
      id: 'moreZuoraApps',
      expandable: true,
      popover: {
        label: i18next.t('GlobalNav.Apps.More'),
        id: 'moreZuoraApps-popover',
        items: [
          {
            label: i18next.t('GlobalNav.Apps.Zephr'),
            icon: 'newWindow',
            href: 'https://www.zuora.com/products/zephr/',
            id: 'Zuora/Zephr',
            chip: '',
          },
          {
            label: i18next.t('GlobalNav.Apps.ZuoraCollections'),
            icon: 'newWindow',
            href: 'https://www.zuora.com/products/collections/',
            id: 'Zuora/Collections',
            chip: '',
          },
          {
            label: i18next.t('GlobalNav.Apps.ZuoraPlatform'),
            icon: 'newWindow',
            href: 'https://www.zuora.com/products/zuora-platform/',
            id: 'Zuora/Platform',
            chip: '',
          },
        ],
      },
    };
  }

  private extractPaymentsItems(
    billingSecondaryItems: NavigationItem[],
  ): NavigationItem[] {
    const paymentsGroupItem = billingSecondaryItems.find(
      (item) => item.id === 'Zuora/Payments',
    );

    // ZPY-248: Extract popover.items children from the Zuora/Payments group for flat rendering.
    // Children have icon: undefined from the transformer so populate icons here.
    return (paymentsGroupItem?.popover?.items ?? []).map((item) => ({
      ...item,
      icon: mapIdToIcon(item.id),
      pinnable: false,
    }));
  }

  private createAppSections(
    billingEntities: TenantInfo[],
    revenueEntities: TenantInfo[],
    allEntities: TenantInfo[],
    billingSecondaryItems: any[],
    revenueSecondaryItems: any[],
    permissions: any,
    billingData: any,
    multiOrgHierarchy?: OrgHierarchyResponse,
    paymentsItems: NavigationItem[] = [],
    showPaymentsApp = false,
  ) {
    const apps: AppSection[] = [];
    console.log(billingEntities);

    // apps.push(AppSectionFactory.createZuoraHelpChat());

    const billingOnlyItems = billingSecondaryItems.filter(
      (item) => item.id !== 'Zuora/Payments',
    );

    if (billingEntities.length && allEntities.length) {
      apps.push(
        AppSectionFactory.createBilling(
          allEntities,
          billingOnlyItems,
          permissions,
          multiOrgHierarchy,
        ),
      );

      if (showPaymentsApp) {
        apps.push(
          AppSectionFactory.createPayments(
            allEntities,
            paymentsItems,
            permissions,
            multiOrgHierarchy,
          ),
        );
      }
    }

    if (revenueSecondaryItems.length) {
      apps.push(
        AppSectionFactory.createRevenue(
          allEntities || [],
          revenueSecondaryItems,
          this.variant,
        ),
      );
    }

    const settingsItems = this.extractSettingsItems(billingData);
    apps.push(
      AppSectionFactory.createSettings(allEntities || [], settingsItems),
    );

    return apps;
  }

  private findCurrentEntityPath(
    entities: TenantInfo[],
  ): TenantInfo[] | undefined {
    const path: TenantInfo[] = [];

    const dfs = (nodes?: TenantInfo[]): TenantInfo[] | undefined => {
      if (!nodes) {
        return undefined;
      }

      for (const node of nodes) {
        path.push(node);
        if (node.isCurrent) {
          return [...path];
        }
        const found = dfs(node.children);
        if (found) {
          return found;
        }
        path.pop();
      }

      return undefined;
    };

    return dfs(entities);
  }

  private findNearestAncestorMatching(
    path: TenantInfo[] | undefined,
    predicate: (entity: TenantInfo) => boolean,
  ): TenantInfo | undefined {
    if (!path || path.length < 2) {
      return undefined;
    }

    for (let index = path.length - 2; index >= 0; index -= 1) {
      const ancestor = path[index];
      if (predicate(ancestor)) {
        return ancestor;
      }
    }

    return undefined;
  }

  private findFirstDescendantMatching(
    entity: TenantInfo | undefined,
    predicate: (candidate: TenantInfo) => boolean,
  ): TenantInfo | undefined {
    if (!entity?.children || entity.children.length === 0) {
      return undefined;
    }

    const queue: TenantInfo[] = [...entity.children];
    while (queue.length > 0) {
      const candidate = queue.shift();
      if (!candidate) {
        continue;
      }

      if (predicate(candidate)) {
        return candidate;
      }

      if (candidate.children) {
        queue.push(...candidate.children);
      }
    }

    return undefined;
  }

  private extractSettingsItems(billingData: any): NavigationItem[] {
    if (this.isRevenueApp && (!billingData || !billingData.settings)) {
      console.log('Revenue app detected, returning hardcoded settings');
      return this.getHardcodedRevenueSettings();
    }
    // Handle null/undefined billingData gracefully
    if (!billingData || !billingData.settings) {
      console.log('No billing settings available, returning empty array');
      return [];
    }

    try {
      const settingsChildren = billingData.settings.filter(
        (item: any) => item.parentId === 'Zuora/Settings',
      );

      const settingsItems: NavigationItem[] = settingsChildren.map((item) => {
        return {
          label: item.label,
          id: item.id,
          icon: undefined,
          href: item.url || undefined,
          pinnable: false,
          chip: '',
        };
      });

      return settingsItems;
    } catch (error) {
      console.warn('⚠️ Error extracting settings items:', error);
      return [];
    }
  }

  private getHardcodedRevenueSettings(): NavigationItem[] {
    return [];
  }

  private getFallbackData(): NavigationData {
    console.warn(
      '⚠️ getFallbackData called - this should not happen with safe navigation',
    );

    // Instead of throwing an error, return a minimal navigation
    return {
      version: '1.0.0',
      primary: {
        top: [
          {
            label: i18next.t('GlobalNav.Apps.ZuoraHome'),
            icon: 'home',
            href: '/',
            id: 'zuora-home',
          },
        ],
        bottom: [],
        user: {
          firstName: '',
          lastName: '',
          email: '',
          role: '',
        },
      },
      secondary: {
        apps: [],
      },
    };
  }
}
